import React, { SyntheticEvent } from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { AnyAction, bindActionCreators } from 'redux';
import {
  formValueSelector,
  getFormSyncErrors,
  getFormAsyncErrors,
  reduxForm
} from 'redux-form';

//@ts-ignore
import SuperFormWizard from '@shorelight/super-form/components/SuperFormWizard';
//@ts-ignore
import ReviewApplication from '@shorelight/super-form/components/ReviewApplication';

import {
  FORM_CONTAINER_NAME,
  FORM_PAGE_NAME,
  FORM_SUBMIT
} from '../../constants';

import { ApplicationState } from '../../store/types';
import { submitApplication } from '../../store/wizard/actions';
import {
  updatePageProgress,
  goToPageApplication,
  reviewApplicationPage,
  highlightMissingFields
} from '../../store/application/actions';

import FormContainerProps from './FormContainer.propTypes';

//HOC from redux-form
const ReduxSuperForm = reduxForm({
  form: FORM_CONTAINER_NAME,
  enableReinitialize: true,
  destroyOnUnmount: false, // set to false to preserve form data when application has multiple pages
  forceUnregisterOnUnmount: true // unregister fields on unmount
})(SuperFormWizard);

const shouldDisableNavigation = (
  currentPageSchema: Page,
  formSyncErrors: FormSyncErrors,
  formAsyncErrors: FormAsyncErrors
): boolean => {
  const isObjectEmpty = (
    objectToCheck: FormSyncErrors | FormAsyncErrors = {}
  ) => Object.entries(objectToCheck).length === 0;

  if (!currentPageSchema.enableValidationNavigation) {
    return false;
  }

  return !(isObjectEmpty(formSyncErrors) && isObjectEmpty(formAsyncErrors));
};

const FormContainer: React.FunctionComponent<FormContainerProps> = props => {
  const handleReviewApplication = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    return props.handleReviewApplication(props.formValues);
  };

  const handleApplicationSubmit = (event: SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();

    return props.handleApplicationSubmit(props.applicationValues);
  };

  if (props.reviewingApplication) {
    return (
      <div data-testid={'review-application'}>
        <ReviewApplication
          hasFooter={true}
          schema={props.schema}
          formValues={props.applicationValues}
          editPageApplication={props.handleEditPage}
          formSubmitName={FORM_SUBMIT}
          handleApplicationSubmit={handleApplicationSubmit}
          formSubmitting={props.submittingApplication}
          formSubmitError={props.applicationSubmitError}
        />
      </div>
    );
  }
  const isNavigationDisabled =
    (props.schema &&
      shouldDisableNavigation(
        props.schema.pages[props.currentPageNumber],
        props.formSyncErrors,
        props.formAsyncErrors
      )) ||
    false;

  return (
    <div data-testid={'full-form'}>
      <ReduxSuperForm
        // @ts-ignore
        dispatch={props.dispatch}
        currentPage={props.currentPageNumber}
        isNavigationDisabled={isNavigationDisabled}
        formPageName={FORM_PAGE_NAME}
        schema={props.schema}
        initialValues={props.applicationValues}
        formValues={props.formValues}
        progressMap={props.progressMap}
        isSaving={false}
        submittingToReview={false}
        formSyncErrors={props.formSyncErrors}
        formAsyncErrors={props.formAsyncErrors}
        handleSelectPage={props.handleSelectPage}
        handleUpdatePageProgress={props.handleUpdatePageProgress}
        handleHighlightMissingFields={props.handleHighlightMissingFields}
        handlePageOnSubmit={handleReviewApplication}
      />
    </div>
  );
};

const selector = formValueSelector(FORM_CONTAINER_NAME); //same as form name

const mapStateToProps = (state: ApplicationState) => {
  const formIds = state.wizard.formIds;
  const schema = state.wizard.schema;
  return {
    schema,
    formValues: formIds && schema ? selector(state, ...formIds) : {},
    formSyncErrors: getFormSyncErrors(FORM_CONTAINER_NAME)(state),
    formAsyncErrors: getFormAsyncErrors(FORM_CONTAINER_NAME)(state),
    currentPageNumber: state.applicationForm.pageNumber,
    progressMap: state.applicationForm.progressMap,
    reviewingApplication: state.applicationForm.reviewingApplication,
    applicationValues: state.applicationForm.values,
    submittingApplication: state.wizard.submittingApplication,
    applicationSubmitError: state.wizard.applicationSubmitError
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<ApplicationState, {}, AnyAction>
) => {
  return {
    dispatch,
    ...bindActionCreators(
      {
        handleReviewApplication: reviewApplicationPage,
        handleUpdatePageProgress: updatePageProgress,
        handleHighlightMissingFields: highlightMissingFields,
        handleSelectPage: goToPageApplication,
        handleEditPage: goToPageApplication,
        handleApplicationSubmit: submitApplication
      },
      dispatch
    )
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(FormContainer);
